/* BN V2: enhances the native Trade form and media-gallery, never sends cart requests. */
if (!customElements.get('bn-product-controls')) {
  customElements.define('bn-product-controls', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('bn-product-info');
      if (!this.root || this.abort) return;
      this.abort = new AbortController();
      this.sticky = this.querySelector('.bn-sticky');
      this.button = this.querySelector('[data-bn-sticky-submit]');
      this.mainButton = this.root.querySelector('[id^="ProductSubmitButton-"]');
      this.form = this.mainButton?.form;
      if (!this.mainButton || !this.form) return;
      this.passed = false;
      this.variantTitle = this.querySelector('[data-bn-sticky-variant]').textContent;
      const options = { signal: this.abort.signal };
      this.root.addEventListener('click', (event) => {
        const tile = event.target.closest('.bn-gallery-tile');
        if (!tile || tile.disabled || !tile.dataset.bnMedia) return;
        this.selectedKind = tile.dataset.bnKind;
        this.root.querySelector('media-gallery')?.setActiveMedia(tile.dataset.bnMedia, false);
        this.syncTiles();
      }, options);
      this.root.addEventListener('slideChanged', (event) => {
        if (!event.target.id?.startsWith('GalleryViewer-')) return;
        const current = event.detail.currentElement;
        if (!current?.dataset.mediaId) return;
        this.root.querySelectorAll('media-gallery li[data-media-id]').forEach(item => item.classList.toggle('is-active', item === current));
        this.syncTiles();
      }, options);
      this.button.addEventListener('click', () => {
        if (this.isDisabled()) return;
        const quantity = this.root.querySelector('.quantity__input');
        if (quantity && !quantity.reportValidity()) {
          quantity.scrollIntoView({ block: 'center' });
          quantity.focus();
          return;
        }
        // Exactly one submit path: Trade product-form owns fetch, loading, errors and drawer.
        this.form.requestSubmit(this.mainButton);
      }, options);
      this.root.addEventListener('bn:variant', ({ detail }) => {
        this.querySelector('.bn-variant-error').hidden = true;
        this.variantTitle = detail.variant?.title || window.variantStrings.unavailable;
        const source = detail.html.querySelector('.bn-media-state');
        const destination = this.root.querySelector('.bn-media-state');
        if (source && destination) destination.replaceWith(source.cloneNode(true));
        this.pendingKind = this.selectedKind || 'gallery_set';
        this.sync();
      }, options);
      this.root.addEventListener('bn:media-updated', () => {
        const tiles = [...this.root.querySelectorAll('.bn-gallery-tile')];
        const target = tiles.find(tile => tile.dataset.bnKind === this.pendingKind && !tile.disabled);
        this.selectedKind = target?.dataset.bnKind || 'gallery_set';
        if (target) this.root.querySelector('media-gallery')?.setActiveMedia(target.dataset.bnMedia, false);
        this.root.querySelector('[id^="GalleryViewer-"]')?.resetPages?.();
        this.syncTiles();
      }, options);
      this.root.addEventListener('bn:variant-error' , () => {
        this.querySelector('.bn-variant-error').hidden = false;
        this.sync();
      }, options);
      document.addEventListener('focusin', () => this.syncVisibility(), options);
      document.addEventListener('focusout', () => queueMicrotask(() => this.syncVisibility()), options);
      window.addEventListener('scroll', () => this.syncVisibility(), { ...options, passive: true });
      window.addEventListener('resize', () => this.syncVisibility(), options);
      // Observe only native state, not our own output (avoids observer loops).
      this.stateObserver = new MutationObserver(() => this.sync());
      this.stateObserver.observe(this.mainButton, { attributes: true, childList: true, subtree: true });
      const price = this.root.querySelector('[id^="price-"]');
      if (price) this.stateObserver.observe(price, { attributes: true, childList: true, subtree: true, characterData: true });
      const mediaList = this.root.querySelector('media-gallery ul');
      if (mediaList) {
        this.mediaObserver = new MutationObserver(() => this.syncTiles());
        this.mediaObserver.observe(mediaList, { attributes: true, attributeFilter: ['class'], subtree: true, childList: true });
      }
      this.intersection = new IntersectionObserver(([entry]) => {
        this.passed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.syncVisibility();
      });
      this.intersection.observe(this.mainButton);
      this.errorUnsubscribe = subscribe(PUB_SUB_EVENTS.cartError, ({ productVariantId } = {}) => {
        if (String(productVariantId) !== this.form.querySelector('[name="id"]')?.value) return;
        this.mainButton.scrollIntoView({ block: 'center' });
      });
      this.sync();
    }
    isDisabled() {
      return this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true' || this.mainButton.classList.contains('loading');
    }
    sync() {
      const price = this.root.querySelector('[id^="price-"]');
      const value = price?.querySelector('.price--on-sale .price__sale .price-item--sale') || price?.querySelector('.price__regular .price-item--regular');
      this.querySelector('[data-bn-sticky-price]').textContent = price?.classList.contains('hidden') ? '' : (value?.textContent.trim() || '');
      this.querySelector('[data-bn-sticky-variant]').textContent = this.variantTitle;
      this.button.disabled = this.isDisabled();
      this.button.textContent = this.mainButton.querySelector('span')?.textContent.trim() || window.variantStrings.addToCart;
      this.button.setAttribute('aria-busy', String(this.mainButton.classList.contains('loading')));
      this.syncVisibility();
    }
    syncVisibility() {
      // A jump from below to above can remain non-intersecting; measure on scroll as well.
      this.passed = this.mainButton.getBoundingClientRect().bottom <= 0;
      const focused = document.activeElement;
      const editing = focused.matches('input:not([type="radio"]):not([type="hidden"]), textarea, select');
      this.sticky.hidden = !this.passed || !window.matchMedia('(max-width:749px)').matches || editing;
    }
    syncTiles() {
      const active = this.root.querySelector('media-gallery .is-active[data-media-id]')?.dataset.mediaId;
      const tiles = [...this.root.querySelectorAll('.bn-gallery-tile')];
      const chosen = tiles.find(t => t.dataset.bnKind === this.selectedKind && t.dataset.bnMedia === active) || tiles.find(t => t.dataset.bnMedia === active);
      tiles.forEach(tile => tile.setAttribute('aria-pressed', String(tile === chosen)));
      const caption = this.root.querySelector('[data-bn-caption]');
      if (!caption) return;
      if (!caption.dataset.setCaption) caption.dataset.setCaption = caption.textContent;
      const kind = chosen?.dataset.bnKind;
      if (kind) this.selectedKind = kind;
      if (kind === 'gallery_set') caption.textContent = caption.dataset.setCaption;
      else if (kind === 'gallery_dimensions') caption.textContent = 'Rysunek wymiarowy przypisany do tego produktu.';
      else if (kind === 'gallery_texture') caption.textContent = this.root.querySelector('.bn-media-state')?.dataset.bnTextureCaption || 'Detal struktury drewna na module z surowego betonu. Pozostałe wykończenia zachowują tę samą fakturę.';
      else if (kind === 'gallery_connection') caption.textContent = 'Detal łączenia modułów betonowych – zdjęcie poglądowe.';
      else caption.textContent = 'Materiał z galerii produktu. Nie potwierdza wybranego wykończenia.';
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.stateObserver?.disconnect(); this.mediaObserver?.disconnect(); this.intersection?.disconnect();
      this.errorUnsubscribe?.();
    }
  });
}
